import { useRouterState } from '@tanstack/react-router';
import { ConfigService } from '@piwikpro/platform';
import { ListSidebarPreference } from '@piwikpro/user-preferences';

export interface UsersFetchParams {
  limit: number
  offset: number
  search: string
  sortBy: string
  sortDirection: string
}

/**
 * Resolves the users-list fetch params (pagination/search/sort) that should be
 * used to refresh the list after an action on a nested route (invite/delete/...).
 *
 * Historically these were passed down as a direct prop from `Users` (the list
 * was an inline child route). With TanStack Router, nested routes render via
 * `<Outlet />` instead, so they're no longer automatically wired - the list
 * hands them over via navigation `state` instead (see `Users.tsx`), falling
 * back to the user's persisted sort preference when navigating here directly.
 */
export const useUsersFetchParams = (
  config: ConfigService,
  userPreferences: any,
): UsersFetchParams => {
  const locationState = useRouterState({
    // TanStack Router always populates `location.state` with its own bookkeeping
    // fields (`__TSR_key`, `__TSR_index`, ...), so it's never `undefined` - it must
    // be checked for the actual fetch params we expect, not just truthiness.
    select: state => state.location.state as Partial<UsersFetchParams> | undefined,
  });

  const listSidebarPreference = new ListSidebarPreference(
    userPreferences.load(),
    '/administration/users',
    {
      sortBy: config.get('USERS_DEFAULT_SORT'),
    },
  );

  const defaults: UsersFetchParams = {
    limit: config.get('SIDEBAR_LISTS_ITEMS_PER_PAGE'),
    offset: 0,
    search: '',
    sortBy: listSidebarPreference.sortBy,
    sortDirection: [config.get('USERS_DEFAULT_SORT'), 'role'].includes(listSidebarPreference.sortBy)
      ? ''
      : '-',
  };

  // Pick only the fetch fields - TanStack always merges `__TSR_*` bookkeeping into
  // `location.state`, and spreading that object into API query params corrupts requests.
  if (typeof locationState?.sortBy === 'string') {
    return {
      limit: Number(locationState.limit) || defaults.limit,
      offset: Number(locationState.offset) || 0,
      search: typeof locationState.search === 'string' ? locationState.search : '',
      sortBy: locationState.sortBy,
      sortDirection: typeof locationState.sortDirection === 'string'
        ? locationState.sortDirection
        : '',
    };
  }

  return defaults;
};
